import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { FAQRows } from '@charitystack/shared';
import PaneSheet, { usePaneSheet } from './PaneSheet';
import { DETAIL_LINK_CLASS } from './DetailDescription';
import useFAQs from '../hooks/useFAQs';

function FAQInlineItem({ faq, isLast, isOverlay }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [contentHeight, setContentHeight] = React.useState(0);
  const contentRef = React.useRef(null);
  const answerId = React.useId();

  React.useLayoutEffect(() => {
    if (!contentRef.current) return undefined;

    const updateContentHeight = () => {
      setContentHeight(contentRef.current?.scrollHeight || 0);
    };

    updateContentHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateContentHeight);
    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [faq.answer]);

  return (
    <div className={`group ${!isLast ? 'border-b border-gray-200' : ''}`}>
      <button
        type="button"
        className={`flex w-full items-center justify-between bg-transparent py-2 text-left ${isOverlay ? 'text-sm' : 'text-xs'} font-medium text-gray-600 cursor-pointer`}
        aria-expanded={isOpen}
        aria-controls={answerId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{faq.question}</span>
        <ChevronDownIcon
          className="faq-inline-chev ml-1.5 h-4 w-4 shrink-0 text-gray-300 transition-transform duration-[var(--cs-motion-fast)] ease-out"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          aria-hidden="true"
        />
      </button>
      <div
        id={answerId}
        className="overflow-hidden"
        style={{
          height: isOpen ? `${contentHeight}px` : '0px',
          transition: 'height var(--cs-motion-base) var(--cs-ease-in-out)',
        }}
        aria-hidden={!isOpen}
      >
        <p
          ref={contentRef}
          className={`text-[color:var(--cs-text-muted)] pb-2 leading-relaxed ${isOverlay ? 'text-sm' : 'text-xs'}`}
        >
          {faq.answer}
        </p>
      </div>
    </div>
  );
}

// The FAQs trigger is a link (SPEC §C, §F2). `triggerColor` from callers is no
// longer read: the brand text role is.
const FAQ_LINK_STYLE = {
  color: 'var(--cs-brand-text, var(--cs-text-primary))',
  textDecorationLine: 'underline',
  textDecorationThickness: '1px',
  textUnderlinePosition: 'from-font',
};

/**
 * Inline FAQ accordion that expands below the action button on detail pages.
 * Renders entirely within the card — no modals, drawers, or portals.
 *
 * Props:
 *   faqs       – array of { question, answer } (optional, skips fetch if provided)
 *   elementID  – fallback ID to fetch FAQs via useFAQs hook
 */
const FAQInlinePanel = ({
  faqs: propFaqs = null,
  elementID,
  formDisplayMode = 'dynamic',
  onToggle,
  onCollapseEnd,
}) => {
  const { faqs: hookFaqs, isLoading, error } = useFAQs(propFaqs ? null : elementID);
  const faqs = propFaqs || hookFaqs;
  const isOverlay = formDisplayMode === 'overlay';

  const [isOpen, setIsOpen] = React.useState(false);
  const contentRef = React.useRef(null);
  /** When grid row is 0fr, scrollHeight is often 0 — keep last good open height for reopen */
  const lastOpenContentHeightRef = React.useRef(0);

  React.useLayoutEffect(() => {
    if (!isOpen || !contentRef.current) return;
    const h = contentRef.current.scrollHeight;
    if (h > 0) lastOpenContentHeightRef.current = h;
  }, [isOpen, faqs, isLoading, error]);

  const toggle = () => {
    const nextOpen = !isOpen;
    let contentHeight = 0;
    if (nextOpen) {
      const raw = contentRef.current?.scrollHeight || 0;
      contentHeight = raw > 0 ? raw : lastOpenContentHeightRef.current;
    }
    setIsOpen(nextOpen);
    if (onToggle) onToggle(nextOpen, contentHeight);
  };

  // Nothing to show
  if (!propFaqs && isLoading) return null;
  if (!propFaqs && error) return null;
  if (!faqs || faqs.length === 0) return null;

  return (
    <div className="detail-faq-inline-root">
      {/* Trigger */}
      <button
        type="button"
        onClick={toggle}
        className="form-back-button detail-faq-inline-trigger gap-1 items-center"
        // CON-514: a link — brand text, always underlined. Inline because
        // `.form-back-button` (unlayered) would out-rank a utility class.
        style={FAQ_LINK_STYLE}
        aria-expanded={isOpen}
      >
        FAQs
        <ChevronDownIcon
          className="h-4 w-4 shrink-0 self-center transition-transform duration-[var(--cs-motion-fast)] ease-out"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          aria-hidden="true"
        />
      </button>

      {/* Inline accordion panel — max-height synced with detail card height easing */}
      <div
        className="faq-inline-panel text-left"
        style={{
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          transition: 'grid-template-rows var(--cs-motion-base) var(--cs-ease-out)',
        }}
        onTransitionEnd={(e) => {
          // Signal parent when the grid collapse animation finishes so it can
          // safely snap the frosted container to its fixed CSS height.
          if (!isOpen && e.propertyName === 'grid-template-rows' && e.target === e.currentTarget) {
            onCollapseEnd?.();
          }
        }}
      >
        <div ref={contentRef} style={{ overflow: 'hidden' }}>
          {faqs.map((faq, index) => (
            <FAQInlineItem
              key={index}
              faq={faq}
              isLast={index === faqs.length - 1}
              isOverlay={isOverlay}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQInlinePanel;

// The overlay pane's FAQs link: 14/20 medium brand text, underlined, with a down chevron.
const FOOTER_LINK_CLASS =
  'inline-flex items-center gap-[var(--cs-space-1)] rounded-[var(--cs-radius-sm)] bg-transparent text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-brand-text,var(--cs-text-primary))] underline decoration-1 [text-underline-position:from-font] focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]';

/**
 * FAQPaneDrawer — the overlay's FAQs (CON-520, Paper 7K5-0 "Overlay · Desktop · FAQs open").
 * The FAQs link sits at the foot of the left pane; it opens the FAQs as a drawer that slides
 * up over the pane (no page scroll): the scrim over the pane, a sheet three quarters of the
 * pane tall with the card-radius top and the sheet shadow, the 52 title strip with a
 * transparent 36 close, and the quiet FAQ rows inside.
 *
 * The parent pane must be `position: relative` and clip its overflow, and should mark the
 * content the drawer covers with `data-pane-sheet-scope`. While open the drawer is modal:
 * Tab wraps inside it, the covered content is inert, and closing returns focus to the
 * FAQs link. Escape closes the drawer, not the overlay: the keydown stops here, before
 * the overlay's window listener.
 *
 * The sheet itself is PaneSheet (modal, Escape, Tab trap, inert scope).
 *
 * Props: faqs (optional; else fetched by elementID, as FAQInlinePanel does); trigger:
 *   'footer' (default) — the overlay pane's centred FAQs row; the drawer renders in place.
 *   'inline' — a plain "FAQs" link the size of the text beside it (the detail pages put it on the
 *     Read more row, DetailMoreRow). The drawer renders into the nearest
 *     [data-pane-sheet-scope] ancestor of the link — the card — so it covers the card, not the row.
 */
export function FAQPaneDrawer({ faqs: propFaqs = null, elementID, trigger = 'footer' }) {
  const { faqs: hookFaqs, isLoading, error } = useFAQs(propFaqs ? null : elementID);
  const faqs = propFaqs || hookFaqs;
  const isInline = trigger === 'inline';
  const sheet = usePaneSheet({ inline: isInline });

  if (!propFaqs && (isLoading || error)) return null;
  if (!Array.isArray(faqs) || faqs.length === 0) return null;

  const link = (
    <button
      ref={sheet.triggerRef}
      type="button"
      onClick={sheet.open}
      aria-expanded={sheet.isOpen}
      aria-haspopup="dialog"
      className={isInline ? DETAIL_LINK_CLASS : FOOTER_LINK_CLASS}
    >
      FAQs
      {!isInline && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="size-[var(--cs-icon)] shrink-0 [stroke-width:var(--cs-icon-stroke)]">
          <path d="m6 9 6 6 6-6" />
        </svg>
      )}
    </button>
  );

  return (
    <>
      {isInline ? link : (
        <div className="flex shrink-0 items-center justify-center border-t border-[color:var(--cs-border-control-subtle)] py-[var(--cs-space-4)]">
          {link}
        </div>
      )}
      {sheet.isOpen && (
        <PaneSheet
          title="Frequently asked questions"
          closeLabel="Close FAQs"
          onClose={sheet.close}
          container={sheet.container}
        >
          <FAQRows faqs={faqs} size="compact" />
        </PaneSheet>
      )}
    </>
  );
}
